import clsx from "clsx";
import { Fragment } from "react";
import { useFormContext } from "react-hook-form";

import type { WizardFormValues } from "../onboardingWizardForm";
import { useCertifications, useOnboarding } from "~/hooks";

const imtRestrictedProfessions = [
	"6d19824b-7f68-4f8f-8c57-e6e0adaff3d1",
	"678bfb4d-88ff-4609-ae9f-31bbe743f537",
	"1bf58c63-ef0a-4306-bad7-f82a87852c4d",
];

const CertificationFields = () => {
	const { certifications } = useCertifications();
	const { onboarding } = useOnboarding();
	const { watch, setValue } = useFormContext<WizardFormValues>();

	const selected = watch("selectedCertificationId");

	const primaryProfessionId = onboarding?.primaryProfessionId;
	const secondaryProfessionId = onboarding?.secondaryProfessionId;

	const isImtDisabled = !!(
		primaryProfessionId &&
		imtRestrictedProfessions.includes(primaryProfessionId) &&
		(!secondaryProfessionId ||
			imtRestrictedProfessions.includes(secondaryProfessionId))
	);

	return (
		<div className="grid grid-cols-1 phablet:grid-cols-2 gap-3">
			{certifications?.map((cert) => {
				const isDisabled =
					isImtDisabled && cert.slug === "integrated-manual-therapist";
				const isSelected = selected === cert._id;

				return (
					<Fragment key={cert._id}>
						<input
							type="radio"
							className="sr-only"
							id={`wizard-cert-${cert._id}`}
							value={cert._id}
							checked={isSelected}
							disabled={isDisabled}
							onChange={() =>
								!isDisabled &&
								setValue("selectedCertificationId", cert._id, {
									shouldDirty: true,
								})
							}
						/>
						<label
							htmlFor={`wizard-cert-${cert._id}`}
							className={clsx(
								"relative flex cursor-pointer flex-col gap-1 rounded-xl border px-3.5 py-2.5 transition-all",
								isDisabled &&
									"pointer-events-none border-transparent bg-slate-50! dark:border-transparent dark:bg-slate-700/50!",
								isSelected &&
									"border-orange-400 bg-orange-50 shadow-sm dark:border-orange-300/40 dark:bg-orange-300/10",
								!isSelected &&
									"border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600 dark:hover:bg-slate-700/60",
							)}
						>
							<span className="font-medium text-orange-700 text-sm dark:text-orange-300">
								{cert.isStudyPlan
									? "Study Plan For:"
									: cert.hasNccaTrack
										? "Certificate"
										: "Certification"}
							</span>

							<span className="font-semibold text-base text-black dark:text-white">
								{cert.isStudyPlan ? "NCCA-accredited CPT Exam" : cert.title}
							</span>

							{isDisabled ? (
								<span className="font-medium text-red-600 text-sm dark:text-red-300">
									Out of scope for your profession
								</span>
							) : cert.focus ? (
								<span className="line-clamp-2 text-slate-700 text-sm dark:text-slate-100">
									{cert.focus}
								</span>
							) : null}
						</label>
					</Fragment>
				);
			})}
		</div>
	);
};

export default CertificationFields;
